import axios from 'axios'
import React, { useCallback, useContext, useMemo, useState, useRef, useEffect } from 'react'
import { createUserWithEmailAndPassword, onAuthStateChanged } from 'firebase/auth'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'react-simple-snackbar'
import { useHistory } from 'react-router-dom'

import { auth } from '../../services/firebase/firebaseConfig'
import { useSelector } from 'react-redux'
import Turnstile, { TurnstileRef } from '../../components/Turnstile'
import { getTurnstileSiteKey } from '../../config/turnstile'
import { getAuthErrorMessage } from '../../helpers/authErrorMessages'
import '../../assets/styles/auth.css'

const SignUp = () => {
    const { userDataLoaded } = useSelector((state: any) => state.user)

    const { t } = useTranslation()

    const PASSWORD_VALIDATIONS = [
        `${t('login.rules.characters')}`,
        `${t('login.rules.number')}`,
        `${t('login.rules.uppercase')}`,
        `${t('login.rules.specialCharacters')}`,
    ]

    const history = useHistory()

    const [openSnackbar] = useSnackbar()
    const [password, setPassword] = useState<string>('')
    const [confirmPassword, setConfirmPassword] = useState<string>('')
    const [email, setEmail] = useState<string>('')
    const [validPassword, setValidPassword] = useState<boolean>(false)
    const [validations, setValidations] = useState<boolean[]>([false, false, false, false])
    const [showPasswordGuid, setShowPasswordGuide] = useState<boolean>(false)
    const [showPassword, setShowPassword] = useState<boolean>(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false)
    // Loading state for registration
    const [isRegistering, setIsRegistering] = useState<boolean>(false)
    // Turnstile captcha state
    const [turnstileToken, setTurnstileToken] = useState<string>('')
    const [turnstileError, setTurnstileError] = useState<boolean>(false)
    // Turnstile widget ref for reset
    const turnstileRef = useRef<TurnstileRef>(null)

    /**
     * Check if user is already authenticated with cached Firebase Auth session
     * If authenticated, redirect to shipments page
     */
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                // User is already logged in with cached session, redirect to shipments
                history.push('/')
            }
        })

        // Cleanup subscription on unmount
        return () => {
            unsubscribe()
        }
    }, [history])

    const checkPassword = useCallback(
        (val: string) => {
            const format = /[ !@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/
            const passwordValidations = [...validations]
            passwordValidations[0] = val.length >= 8
            passwordValidations[1] = /\d/.test(val)
            passwordValidations[2] = /[A-Z]/.test(val)
            passwordValidations[3] = format.test(val)

            const invalid = passwordValidations.findIndex((valid) => !valid)

            setValidations(passwordValidations)
            if (invalid > -1) {
                setValidPassword(false)
            } else {
                setValidPassword(true)
            }
        },
        [validations]
    )

    const handleEmail = useCallback(
        (e: any) => {
            setEmail(e.target.value)
        },
        [setEmail]
    )

    const handlePassword = useCallback(
        (e: any) => {
            if (e.target.value) setShowPasswordGuide(true)
            setPassword(e.target.value)
            checkPassword(e.target.value)
        },
        [setPassword, checkPassword]
    )

    const handleConfirmPassword = useCallback(
        (e: any) => {
            setConfirmPassword(e.target.value)
        },
        [setConfirmPassword]
    )

    const handleRegister = useCallback(async () => {
        try {
            setIsRegistering(true)
            if (!turnstileToken) {
                openSnackbar('Please complete the verification challenge')
                setIsRegistering(false)
                return
            }

            // First, check if user exists in USERS collection (admin must add them first)
            const checkUserLink = process.env.REACT_APP_CHECK_USER_EXISTS || ''
            
            if (!checkUserLink) {
                openSnackbar('Configuration error. Please contact support.')
                setIsRegistering(false)
                return
            }

            let userCheckResult
            try {
                userCheckResult = await axios({
                    url: checkUserLink,
                    method: 'POST',
                    data: {
                        email: email,
                        customerId: process.env.REACT_APP_CLIENT,
                    },
                })
            } catch (error: any) {
                openSnackbar('Unable to verify user. Please try again.')
                setIsRegistering(false)
                return
            }

            // Check if user exists in USERS collection
            if (!userCheckResult?.data?.exists) {
                // User hasn't been added by admin yet
                openSnackbar(
                    'This email is not registered in the system. Please contact your administrator to be added first.'
                )
                setIsRegistering(false)
                return
            }

            // Check if user already has an auth account
            if (userCheckResult?.data?.hasAuthAccount) {
                openSnackbar('An account with this email already exists. Please login instead.')
                history.push('/login')
                setIsRegistering(false)
                return
            }

            // Check if user has a role assigned
            if (!userCheckResult?.data?.hasRole) {
                openSnackbar('Your account exists but no role has been assigned. Please contact your administrator.')
                setIsRegistering(false)
                return
            }

            // Check if user is active
            if (!userCheckResult?.data?.isActive) {
                openSnackbar('Your account is not active. Please contact your administrator.')
                setIsRegistering(false)
                return
            }

            // User is verified and can proceed with Firebase Auth account creation
            // Store Turnstile token in sessionStorage for validation
            sessionStorage.setItem('turnstileToken', turnstileToken)
            
            await createUserWithEmailAndPassword(auth, email, password)
            history.push('/shipments')
        } catch (error: any) {
            // Get user-friendly error message
            const errorMessage = getAuthErrorMessage(error)
            openSnackbar(errorMessage)
            // Clear token on error
            sessionStorage.removeItem('turnstileToken')
            // Clear local token state
            setTurnstileToken('')
            // Reset Turnstile widget to generate new token
            turnstileRef.current?.reset()
        } finally {
            setIsRegistering(false)
        }
    }, [email, password, turnstileToken, history])

    const showSsoInfo = useCallback(() => {
        if (!turnstileToken) {
            openSnackbar('Please complete the verification challenge first')
            return
        }
        openSnackbar(
            'Please contact your organization and request Single Sign-On (SSO) to be set up for your Shyftbase instance.'
        )
    }, [turnstileToken])

    const disableButton = useMemo(() => {
        return !email || !validPassword || password !== confirmPassword || !turnstileToken
    }, [email, validPassword, password, confirmPassword, turnstileToken])

    /**
     * Handle Turnstile verification success
     */
    const handleTurnstileVerify = useCallback((token: string) => {
        setTurnstileToken(token)
        setTurnstileError(false)
    }, [])
    /**
     * Handle Turnstile verification error
     */
    const handleTurnstileError = useCallback(() => {
        setTurnstileToken('')
        setTurnstileError(true)
        openSnackbar('Verification failed. Please try again.')
    }, [])

    /**
     * Handle Turnstile token expiration
     */
    const handleTurnstileExpire = useCallback(() => {
        setTurnstileToken('')
        openSnackbar('Verification expired. Please verify again.')
    }, [])

    const renderValidations = useCallback(
        (item: any, index: number) => {
            return (
                <div className={validations[index] ? 'tag m-1 is-primary' : 'tag m-1'}>
                    <p key={item} className="is-size-7">
                        {item}
                    </p>
                </div>
            )
        },
        [validations]
    )

    // Randomized background spacing/lines per mount (visual only)
    const gridX = useMemo(() => Math.floor(52 + Math.random() * 20), [])
    const gridY = useMemo(() => Math.floor(52 + Math.random() * 20), [])
    const lineV1 = useMemo(() => `${20 + Math.floor(Math.random() * 12)}%`, [])
    const lineV2 = useMemo(() => `${28 + Math.floor(Math.random() * 12)}%`, [])
    const lineV3 = useMemo(() => `${10 + Math.floor(Math.random() * 10)}%`, [])
    const lineV4 = useMemo(() => `${55 + Math.floor(Math.random() * 10)}%`, [])
    const lineV5 = useMemo(() => `${75 + Math.floor(Math.random() * 10)}%`, [])
    const lineH1 = useMemo(() => `${45 + Math.floor(Math.random() * 10)}%`, [])
    const lineH2 = useMemo(() => `${25 + Math.floor(Math.random() * 10)}%`, [])
    const lineH3 = useMemo(() => `${65 + Math.floor(Math.random() * 10)}%`, [])

    return (
        <section
            className="hero is-fullheight auth-hero auth-hero--cosmic-light"
            style={{
                ['--auth-grid-size-x' as any]: `${gridX}px`,
                ['--auth-grid-size-y' as any]: `${gridY}px`,
                ['--line-v1-left' as any]: lineV1,
                ['--line-v2-right' as any]: lineV2,
                ['--line-v3-left' as any]: lineV3,
                ['--line-v4-left' as any]: lineV4,
                ['--line-v5-left' as any]: lineV5,
                ['--line-h1-top' as any]: lineH1,
                ['--line-h2-top' as any]: lineH2,
                ['--line-h3-top' as any]: lineH3,
            }}
        >
            <div className="auth-stars" />
            <div className="auth-bg-layer">
                <div className="auth-floating-dots">
                    <div className="auth-dot auth-dot-1" />
                    <div className="auth-dot auth-dot-2" />
                    <div className="auth-dot auth-dot-3" />
                    <div className="auth-dot auth-dot-4" />
                </div>
                <div className="auth-lines">
                    <div className="auth-line-v-1" />
                    <div className="auth-line-v-2" />
                    <div className="auth-line-v-3" />
                    <div className="auth-line-v-4" />
                    <div className="auth-line-v-5" />
                    <div className="auth-line-h-1" />
                    <div className="auth-line-h-2" />
                    <div className="auth-line-h-3" />
                </div>
                <div className="auth-grid" />
            </div>
            <div className="hero-body">
                <div className="container">
                    <div className="columns is-centered">
                        <div className="column is-4-desktop is-6-tablet is-12-mobile">
                            <div
                                className="card p-4"
                                style={{ borderRadius: 12, boxShadow: '0 12px 30px rgba(0,0,0,0.3)' }}
                            >
                                <div className="has-text-centered mb-3">
                                    <img
                                        src="https://firebasestorage.googleapis.com/v0/b/public-contents.appspot.com/o/SHYFTBASE%2FShyftBase%20logo%20nobkg.png?alt=media&token=79f9a3e0-1513-4879-a5cb-a4155462798b"
                                        alt="shyftbase logo"
                                        width={60}
                                        style={{ filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.2))' }}
                                    />
                                    <h1 className="title is-5 mt-2 mb-3">Create your account</h1>
                                </div>

                                {!userDataLoaded && (
                                    <>
                                        <div className="field">
                                            <label className="label is-size-7 has-text-grey has-text-left">Email</label>
                                            <div className="control has-icons-left">
                                                <input
                                                    className="input"
                                                    type="email"
                                                    placeholder="name@company.com"
                                                    onChange={handleEmail}
                                                />
                                                <span className="icon is-small is-left">
                                                    <i className="fas fa-envelope" />
                                                </span>
                                            </div>
                                        </div>

                                        <div className="field">
                                            <div className="is-flex is-justify-content-space-between is-align-items-center">
                                                <label className="label is-size-7 has-text-grey has-text-left">
                                                    {t('login.messages.createPassword')}
                                                </label>
                                                <button
                                                    type="button"
                                                    className="button is-white is-small"
                                                    onClick={() => setShowPassword((p) => !p)}
                                                    style={{ textDecoration: 'underline' }}
                                                >
                                                    {showPassword ? 'Hide' : 'Show'}
                                                </button>
                                            </div>
                                            <div className="control has-icons-left">
                                                <input
                                                    className="input"
                                                    id="password"
                                                    type={showPassword ? 'text' : 'password'}
                                                    placeholder="Password"
                                                    onChange={handlePassword}
                                                    onFocus={handlePassword}
                                                />
                                                <span className="icon is-small is-left">
                                                    <i className="fas fa-lock" />
                                                </span>
                                            </div>
                                            {showPasswordGuid && (
                                                <div className="is-flex is-flex-wrap-wrap mt-2">
                                                    {PASSWORD_VALIDATIONS.map(renderValidations)}
                                                </div>
                                            )}
                                        </div>

                                        <div className="field">
                                            <div className="is-flex is-justify-content-space-between is-align-items-center">
                                                <label className="label is-size-7 has-text-grey has-text-left">
                                                    {t('login.messages.confirmPassword')}
                                                </label>
                                                <button
                                                    type="button"
                                                    className="button is-white is-small"
                                                    onClick={() => setShowConfirmPassword((p) => !p)}
                                                    style={{ textDecoration: 'underline' }}
                                                >
                                                    {showConfirmPassword ? 'Hide' : 'Show'}
                                                </button>
                                            </div>
                                            <div className="control has-icons-left">
                                                <input
                                                    className="input"
                                                    id="confirmPassword"
                                                    type={showConfirmPassword ? 'text' : 'password'}
                                                    placeholder="Confirm password"
                                                    onChange={handleConfirmPassword}
                                                    onFocus={handleConfirmPassword}
                                                />
                                                <span className="icon is-small is-left">
                                                    <i className="fas fa-lock" />
                                                </span>
                                            </div>
                                            {confirmPassword.length > 0 && password !== confirmPassword && (
                                                <p className="is-size-7 has-text-danger mt-2">
                                                    {t('login.messages.notMatch')}
                                                </p>
                                            )}
                                        </div>

                                        <div className="field mt-3">
                                            <button
                                                id="sign-up"
                                                className={`button is-primary is-fullwidth ${
                                                    disableButton ? 'is-disabled' : ''
                                                } ${isRegistering ? 'is-loading' : ''}`}
                                                onClick={handleRegister}
                                                disabled={disableButton || isRegistering}
                                            >
                                                {t('login.messages.createAccount')}
                                            </button>
                                        </div>

                                        <div className="field mt-3">
                                            <div className="has-text-centered">
                                                <Turnstile
                                                    ref={turnstileRef}
                                                    siteKey={getTurnstileSiteKey()}
                                                    onVerify={handleTurnstileVerify}
                                                    onError={handleTurnstileError}
                                                    onExpire={handleTurnstileExpire}
                                                    theme="light"
                                                    action="signup"
                                                />
                                                {turnstileError && (
                                                    <p className="help is-danger mt-1">
                                                        Verification failed. Please try again.
                                                    </p>
                                                )}
                                            </div>
                                        </div>

                                        <div className="has-text-centered my-3">
                                            <span className="is-size-7 has-text-grey">or continue with</span>
                                        </div>

                                        <div className="columns is-mobile is-variable is-2">
                                            <div className="column">
                                                <button
                                                    type="button"
                                                    className={`button is-light is-fullwidth ${!turnstileToken ? 'is-disabled' : ''}`}
                                                    onClick={showSsoInfo}
                                                    disabled={!turnstileToken}
                                                >
                                                    <span className="icon">
                                                        <svg
                                                            width="18"
                                                            height="18"
                                                            viewBox="0 0 48 48"
                                                            fill="none"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <path
                                                                fill="#EA4335"
                                                                d="M24 9.5c3.94 0 7.1 1.63 9.26 3l6.8-6.8C36.9 2.33 31.9 0 24 0 14.62 0 6.48 5.38 2.56 13.14l7.9 6.14C12.3 13.2 17.62 9.5 24 9.5z"
                                                            />
                                                            <path
                                                                fill="#34A853"
                                                                d="M46.5 24c0-1.62-.14-3.2-.41-4.71H24v9h12.7c-.55 2.97-2.2 5.48-4.7 7.17l7.2 5.58C43.9 37.65 46.5 31.3 46.5 24z"
                                                            />
                                                            <path
                                                                fill="#4A90E2"
                                                                d="M10.46 28.28A14.5 14.5 0 019.5 24c0-1.48.25-2.9.7-4.22l-7.9-6.14A23.93 23.93 0 000 24c0 3.83.91 7.43 2.56 10.6l7.9-6.32z"
                                                            />
                                                            <path
                                                                fill="#FBBC05"
                                                                d="M24 48c6.48 0 11.9-2.15 15.86-5.85l-7.2-5.58c-2.02 1.36-4.6 2.16-8.66 2.16-6.38 0-11.7-3.7-13.54-8.64l-7.9 6.32C6.48 42.62 14.62 48 24 48z"
                                                            />
                                                        </svg>
                                                    </span>
                                                    <span>Google</span>
                                                </button>
                                            </div>
                                            <div className="column">
                                                <button
                                                    type="button"
                                                    className={`button is-light is-fullwidth ${!turnstileToken ? 'is-disabled' : ''}`}
                                                    onClick={showSsoInfo}
                                                    disabled={!turnstileToken}
                                                >
                                                    <span className="icon">
                                                        <svg
                                                            width="18"
                                                            height="18"
                                                            viewBox="0 0 23 23"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <rect width="10" height="10" x="1" y="1" fill="#F25022" />
                                                            <rect width="10" height="10" x="12" y="1" fill="#7FBA00" />
                                                            <rect width="10" height="10" x="1" y="12" fill="#00A4EF" />
                                                            <rect width="10" height="10" x="12" y="12" fill="#FFB900" />
                                                        </svg>
                                                    </span>
                                                    <span>Microsoft</span>
                                                </button>
                                            </div>
                                        </div>

                                        <div className="has-text-centered mt-4">
                                            <p className="is-size-7 has-text-grey mb-2">
                                                <a
                                                    href="https://www.iubenda.com/privacy-policy/76423059"
                                                    className="iubenda-white iubenda-noiframe iubenda-embed iubenda-noiframe"
                                                    title="Privacy Policy "
                                                >
                                                    Privacy Policy
                                                </a>
                                                {' • '}
                                                © {new Date().getFullYear()} Shyftbase Inc.
                                            </p>
                                        </div>

                                        <a href="/login" className="button is-white is-fullwidth mt-2">
                                            Already have an account? Log in
                                        </a>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default React.memo(SignUp)